import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { badgeContrato, derivarStageExibicao, diasParado, STAGES } from "../lib/stages";
import type { BoardRow } from "../lib/types";
import { BotaoLinkAssinatura } from "./BotaoLinkAssinatura";
import styles from "./BoardTable.module.css";

type Coluna = "nome" | "etapa" | "status" | "valor" | "parado";

const STAGE_LABEL = new Map(STAGES.map((s) => [s.n, s.label]));

export function BoardTable({ rows, mostrarValor }: { rows: BoardRow[]; mostrarValor: boolean }) {
  const nav = useNavigate();
  const [ordenarPor, setOrdenarPor] = useState<Coluna>("parado");
  const [asc, setAsc] = useState(false);

  const ordenadas = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      let va: number | string = 0;
      let vb: number | string = 0;
      switch (ordenarPor) {
        case "nome":
          va = (a.nome_projeto ?? a.cliente_nome ?? "").toLowerCase();
          vb = (b.nome_projeto ?? b.cliente_nome ?? "").toLowerCase();
          break;
        case "etapa": va = derivarStageExibicao(a); vb = derivarStageExibicao(b); break;
        case "status": va = a.contrato_status ?? ""; vb = b.contrato_status ?? ""; break;
        case "valor": va = a.valor_mensal_numero ?? 0; vb = b.valor_mensal_numero ?? 0; break;
        case "parado": va = diasParado(a.atualizado_em) ?? -1; vb = diasParado(b.atualizado_em) ?? -1; break;
      }
      if (va < vb) return asc ? -1 : 1;
      if (va > vb) return asc ? 1 : -1;
      return 0;
    });
    return arr;
  }, [rows, ordenarPor, asc]);

  function th(col: Coluna, label: string) {
    return (
      <th
        className={styles.th}
        onClick={() => {
          if (ordenarPor === col) setAsc(!asc);
          else { setOrdenarPor(col); setAsc(false); }
        }}
      >
        {label}
        {ordenarPor === col && <span className={styles.arrow}>{asc ? "▲" : "▼"}</span>}
      </th>
    );
  }

  return (
    <div className={styles.wrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            {th("nome", "Cliente")}
            {th("etapa", "Etapa")}
            {th("status", "Contrato")}
            {mostrarValor && th("valor", "Valor/mês")}
            {th("parado", "Parado há")}
          </tr>
        </thead>
        <tbody>
          {ordenadas.map((r) => {
            const st = derivarStageExibicao(r);
            const badge = badgeContrato(r.contrato_status, r.contrato_confirmado_em);
            const dias = diasParado(r.atualizado_em);
            return (
              <tr key={r.cliente_id} className={styles.row} onClick={() => nav(`/clientes/${r.cliente_id}`)}>
                <td className={styles.nome}>{r.nome_projeto ?? r.cliente_nome ?? "Sem nome"}</td>
                <td>
                  <span className={styles.etapa}>{st}</span>
                  <span className="dim"> · {STAGE_LABEL.get(st)}</span>
                </td>
                <td>
                  <span className={`badge badge-${badge.tone}`}>{badge.label}</span>
                  {r.n_contratos > 1 && <span className="dim" style={{ marginLeft: 6, fontSize: 12 }}>·{r.n_contratos}</span>}
                  {r.d4sign_document_uuid && (r.contrato_status === "aguardando_assinatura" || r.contrato_status === "em_revisao_interna") && (
                    <span style={{ marginLeft: 8 }}>
                      <BotaoLinkAssinatura doc={r.d4sign_document_uuid} clientNome={r.cliente_nome} compacto />
                    </span>
                  )}
                </td>
                {mostrarValor && (
                  <td className="mono">{r.valor_mensal_numero != null ? `R$ ${Number(r.valor_mensal_numero).toLocaleString("pt-BR")}` : "—"}</td>
                )}
                <td className={dias != null && dias >= 7 ? styles.parado : "dim"}>
                  {dias != null ? `${dias}d` : "—"}
                </td>
              </tr>
            );
          })}
          {ordenadas.length === 0 && (
            <tr><td colSpan={mostrarValor ? 5 : 4} className={styles.empty}>Nenhum cliente com os filtros atuais.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
